"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CorePlayer = exports.proxyHandler = exports.timeupdateWrapper = void 0;
const tslib_1 = require("tslib");
const log_client_1 = require("@video/log-client");
const mobx_1 = require("mobx");
const api_1 = require("../../api");
const adapter_1 = require("../../api/adapter");
const feature_1 = require("../../api/adapter/features/feature");
const bitrate_switching_1 = require("../../api/player/features/bitrate-switching");
const feature_2 = require("../../api/player/features/feature");
const video_element_1 = require("../../api/typings/video-element");
const errors_1 = require("../errors");
const errors_deprecated_1 = require("../errors-deprecated");
const mediasoup_source_1 = require("../mediasoup-source");
const context_1 = require("../utils/context/context");
const play_logs_1 = require("../utils/debug/play-logs");
const event_emitter_1 = require("../utils/events/event-emitter");
const sync_handler_1 = require("../utils/proxy/sync-handler");
const timeupdate_wrapper_1 = require("../utils/timeupdate-wrapper");
const helper_1 = require("./helper");
const media_element_supervisor_1 = require("./media-element-supervisor");
const auth_core_1 = tslib_1.__importDefault(require("../utils/auth/auth-core"));
const request_1 = require("../utils/request/request");
const BACK_OFF = [0, 1000, 2000, 5000, 25000];
const emptyCounters = {
    bufferOverflowCount: 0,
    bufferUnderflowCount: 0,
    currentErrorCount: 0,
    downshift: 0,
    fragCounts: 0,
    fragDownloadTime: 0,
    fragDuration: 0,
    fragMaxTime: 0,
    fragMinTime: 0,
    fragFetchTime: 0,
    fragSize: 0,
    lastProgress: 0,
    recentErrorCount: 0,
    restartCount: 0,
    upshift: 0,
};
exports.timeupdateWrapper = new timeupdate_wrapper_1.TimeupdateWrapper();
exports.proxyHandler = new sync_handler_1.SyncHandler({
    logger: new log_client_1.LoggerCore("VDC-core"),
    methods: {
        isSupported: "postpone",
        initVideoEl: "postpone",
        getClosestQuality: "stub",
    },
});
const defaultOptions = {
    bitrate: null,
    muted: false,
    volume: 0.75,
    autoPlay: true,
    origin: false,
    preset: null,
    recoverErrorCount: 0,
    timeout: 30000,
    driverFailover: true,
};
class CorePlayer extends event_emitter_1.ObservableEventEmitter {
    get logger() {
        return this.ctx.logger;
    }
    get poster() {
        return this._poster;
    }
    set poster(val) {
        if (val != null && this.elementSupervisor != null) {
            this.elementSupervisor.setPoster(val);
        }
        this._poster = val;
    }
    /**
     * @deprecated Use `currentQuality` instead
     */
    get activeLayer() {
        var _a, _b;
        return (_b = (_a = this.currentQuality) === null || _a === void 0 ? void 0 : _a.layer) !== null && _b !== void 0 ? _b : null;
    }
    /**
     * @deprecated Use `availableQualities` instead
     */
    get layers() {
        return (0, helper_1.getBitrateLayersFromQualities)(this.availableQualities);
    }
    constructor(ctx, provider, options) {
        var _a, _b;
        super();
        this.counters = emptyCounters;
        this.lockRestart = false;
        this.clearRestartCount = 0;
        this.playingPromise = null;
        this.elementSupervisor = null;
        this.consumerAudioMuted = undefined;
        this.consumerVideoPaused = undefined;
        this.lowPowerMode = false;
        this.internalPaused = false;
        this.qualityEqual = false;
        this.internalMuted = false;
        this.internalVolume = 1;
        this.startedPlaying = false;
        this.pausedOnUnmount = false;
        this.mutedOnHidden = false;
        this.autoPlay = true;
        this.driverFailover = true;
        this.forcedMute = false;
        this.source = null;
        this.errors = {};
        this.availableQualities = [];
        this.currentQuality = null;
        this.isConnecting = null;
        this.isConnected = null;
        this.blurred = false;
        this.qualityHasChanged = false;
        this.successfulPlayTimeStamp = null;
        this._poster = null;
        this.preferredLevel = bitrate_switching_1.AutoQualityLevel.Auto;
        (0, mobx_1.makeObservable)(this, {
            // observables
            consumerAudioMuted: mobx_1.observable,
            consumerVideoPaused: mobx_1.observable,
            internalPaused: mobx_1.observable,
            internalMuted: mobx_1.observable,
            internalVolume: mobx_1.observable,
            forcedMute: mobx_1.observable,
            source: mobx_1.observable.ref,
            availableQualities: mobx_1.observable.shallow,
            currentQuality: mobx_1.observable.shallow,
            _poster: mobx_1.observable,
            driverFailover: mobx_1.observable,
            blurred: mobx_1.observable,
            preferredLevel: mobx_1.observable,
            lowPowerMode: mobx_1.observable,
            // computed
            attached: mobx_1.computed,
            localVideoPaused: mobx_1.computed,
            localAudioMuted: mobx_1.computed,
            localAudioVolume: mobx_1.computed,
            poster: mobx_1.computed,
            // actions
            attachTo: mobx_1.action,
        });
        this.ctx = ctx;
        this.options = options !== null && options !== void 0 ? options : defaultOptions;
        if (this.options.autoPlay != null) {
            this.autoPlay = this.options.autoPlay;
        }
        this.provider = provider;
        this.internalPaused = !this.autoPlay;
        if (((_a = this.options) === null || _a === void 0 ? void 0 : _a.muted) != null) {
            this.internalMuted = this.options.muted;
        }
        if (((_b = this.options) === null || _b === void 0 ? void 0 : _b.volume) != null) {
            this.localAudioVolume = this.options.volume;
        }
        if (this.ctx.token != null) {
            const bootstrap = typeof this.ctx.token === "string" ? { token: this.ctx.token } : { refreshToken: this.ctx.token };
            // should this Auth handle w/ endpoints as well? Where should that be configured?
            this.auth = new auth_core_1.default({
                bootstrap,
            });
        }
        this.addInnerDisposer((0, mobx_1.reaction)(() => [this.availableQualities, this.preferredLevel], ([qly, lvl]) => {
            var _a, _b, _c;
            // TODO: Remove level from being null and set it to be an automatic default quality
            // Checking for quality length === 1 to see if we are using a substitute stream or not
            // Substitute streams only provide one encoding
            if (lvl == null && ((qly === null || qly === void 0 ? void 0 : qly.length) === 1 || this.format === "flv-http")) {
                lvl = bitrate_switching_1.TranscodeScoreLevel.Highest;
            }
            else if (lvl == null && (qly === null || qly === void 0 ? void 0 : qly.length) !== 1) {
                // We set the quality back to null when the substitute stream is removed
                this.currentQuality = null;
                return;
            }
            if (lvl != null && qly != null) {
                const closestQuality = (0, helper_1.findClosestQuality)(lvl, qly);
                if (closestQuality !== this.currentQuality) {
                    this.qualityHasChanged = true;
                    ctx.logger.debug("quality change", {
                        format: this.format,
                        oldQuality: (_b = (_a = this.currentQuality) === null || _a === void 0 ? void 0 : _a.level) !== null && _b !== void 0 ? _b : null,
                        newQuality: (_c = closestQuality === null || closestQuality === void 0 ? void 0 : closestQuality.level) !== null && _c !== void 0 ? _c : null,
                        preferredQuality: lvl,
                    });
                }
                this.currentQuality = closestQuality;
                if (this.qualityHasChanged) {
                    this.updatePosterWithLatestFrame();
                }
                if (this.currentQuality != null && this.provider.source != null) {
                    this.handleSource(this.provider.source);
                }
            }
        }, {
            equals: mobx_1.comparer.structural,
        }));
        ctx.logger.attachObject(this);
        ctx.logger.trace("constructor()", {
            options: this.options,
        });
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`Core Player Context Cancelled: ${reason}`));
        this._handleVisibilityChange = () => {
            if (adapter_1.device.isIosDevice || adapter_1.device.isAndroidDevice) {
                if (adapter_1.device.hidden) {
                    this.mutedOnHidden = this.internalMuted;
                    this.localAudioMuted = true;
                }
                else {
                    this.localAudioMuted = this.mutedOnHidden;
                }
            }
        };
        adapter_1.device.addEventListener("visibilitychange", this._handleVisibilityChange);
        // this.hasAudio = source.hasAudio;
        // this.hasVideo = source.hasVideo;
        this.provider.on("source", this.handleSource);
        this.provider.on("source", this.isSource);
        this.provider.on("audioMuted", this.handleAudioMuted);
        this.provider.on("videoPaused", this.handleVideoPaused);
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            this.provider.on("accessDenied", this.handleProviderAccessDenied);
        }
        this.addInnerDisposer(() => {
            this.provider.off("source", this.handleSource);
            this.provider.off("source", this.isSource);
            this.provider.off("audioMuted", this.handleAudioMuted);
            this.provider.off("videoPaused", this.handleVideoPaused);
            adapter_1.device.removeEventListener("visibilitychange", this._handleVisibilityChange);
            if (this.auth != null) {
                this.auth.destroy();
            }
            if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
                this.provider.off("accessDenied", this.handleProviderAccessDenied);
            }
        });
        if (this.provider.source != null) {
            const source = this.provider.source;
            // we're unable to call virtual methods in constructor
            // but ts/js never throws any error if we do so :(
            // @todo find a way to prevent this
            setTimeout(() => {
                this.handleSource(source);
            }, 0);
        }
    }
    updatePosterWithLatestFrame() {
        if (this.elementSupervisor != null && adapter_1.device.isImplements(feature_1.Feature.CREATE_SCREENSHOT)) {
            this.poster = this.elementSupervisor.createScreenshot();
        }
        else {
            this.ctx.logger.warn("Unable to set update poster. Create screenshot is not compatible with device.");
        }
    }
    async ready() {
        if (this.source != null) {
            return;
        }
        this.ctx.logger.debug("no source, not ready");
        // why linter triggers this error?
        return new Promise((resolve, reject) => {
            let timeoutHandler;
            const dispose = (0, mobx_1.autorun)((r) => {
                if (this.source != null) {
                    r.dispose();
                    adapter_1.device.clearTimeout(timeoutHandler);
                    resolve();
                }
            });
            if (this.options.timeout != null) {
                timeoutHandler = adapter_1.device.setTimeout(() => {
                    dispose();
                    const err = new errors_deprecated_1.PlayingIssueError(`timeout after ${this.options.timeout}ms`, {
                        player: this,
                    });
                    this.emitErrorDeprecated(err);
                    if (this.options.driverFailover) {
                        this.emit("driverFailover", true);
                    }
                    reject(err);
                }, this.options.timeout);
            }
        });
    }
    handleAudioMuted(val) {
        this.consumerAudioMuted = val;
    }
    handleVideoPaused(val) {
        this.consumerVideoPaused = val;
    }
    handleProviderAccessDenied(val) {
        this.emit("playerAccessDenied", val);
    }
    isSource() {
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            this.consumerVideoPaused = !this.provider.hasVideo || this.provider.videoPaused;
        }
    }
    get attached() {
        return this.elementSupervisor != null;
    }
    get localVideoPaused() {
        return this.internalPaused;
    }
    set localVideoPaused(val) {
        this.internalPaused = val;
        if (this.elementSupervisor != null) {
            if (this.internalPaused) {
                this.pauseEl();
            }
            else {
                (async () => {
                    try {
                        await this.play();
                    }
                    catch (err) {
                        this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("VideoElement.play() error", {
                            inner: err instanceof Error ? err : null,
                            player: this,
                        }));
                    }
                })();
            }
        }
    }
    pauseEl() {
        var _a;
        (_a = this.elementSupervisor) === null || _a === void 0 ? void 0 : _a.pause();
    }
    get localAudioMuted() {
        return this.internalMuted;
    }
    set localAudioMuted(val) {
        this.internalMuted = val;
        if (this.elementSupervisor != null) {
            this.elementSupervisor.muted = val;
        }
    }
    get localAudioVolume() {
        return this.internalVolume;
    }
    set localAudioVolume(val) {
        if (val > 1 || val < 0) {
            this.ctx.logger.warn(`Volume level not set. ${val} is not a valid volume value for an HTMLMediaElement. Volume levels must be between 0-1.`);
            return;
        }
        this.internalVolume = val;
        if (this.elementSupervisor != null) {
            this.elementSupervisor.volume = val;
        }
    }
    handleElPlay() {
        if (this.suspended) {
            return;
        }
        if (this.internalPaused) {
            this.ctx.logger.warn("A try to call .play() on HTMLVideoElement outside the Player");
            // VC-1686: Paused streams, on Safari, automatically resume when leaving the page and then returning to the page
            if (adapter_1.device.isSafari) {
                this.localVideoPaused = true;
            }
            else {
                this.internalPaused = false;
            }
        }
        else {
            this.ctx.logger.info(".play() called on HTMLVideoElement");
        }
    }
    handleElPause() {
        if (this.suspended) {
            return;
        }
        if (!this.internalPaused && this.elementSupervisor != null) {
            if (!this.elementSupervisor.paused) {
                return;
            }
            // no warnings because browser can do that
            this.play().catch((err) => {
                adapter_1.device.setTimeout(this.handleElPause, 50);
                this.ctx.logger.error("An error occured on the play() request", { err: (0, errors_deprecated_1.wrapNativeError)(err) });
            });
        }
    }
    handleElError() {
        var _a, _b;
        if (this.elementSupervisor == null || this.suspended) {
            return;
        }
        const err = video_element_1.MediaErrorCodeConstants[(_b = (_a = this.elementSupervisor.error) === null || _a === void 0 ? void 0 : _a.code) !== null && _b !== void 0 ? _b : 0];
        this.ctx.logger.warn(`VideoElement Error: ${err}`);
    }
    handleElVolumeChange() {
        if (this.elementSupervisor == null || this.suspended) {
            return;
        }
        if (Math.abs(this.internalVolume - this.elementSupervisor.volume) > 0.01) {
            this.ctx.logger.warn("A try to change volume on HTMLVideoElement outside the Player");
            this.elementSupervisor.volume = this.internalVolume;
        }
        if (this.elementSupervisor.muted !== this.internalMuted) {
            this.elementSupervisor.muted = this.internalMuted;
        }
    }
    async attachTo(el) {
        this.ctx.logger.trace("attachTo()", { el: (0, play_logs_1.dumpVideoElement)(el) });
        this.elementSupervisor = media_element_supervisor_1.MediaElementSupervisor.getSupervisor(el).attachToPlayer(this);
        this.pausedOnUnmount = this.internalPaused;
        this.ctx.logger.debug("attach to host element", { element: (0, play_logs_1.dumpVideoElement)(el), options: this.options });
        this.initVideoEl(this.elementSupervisor).catch((err) => this.ctx.logger.error(`Unable to initialize VideoElement: ${err}`));
        if (this.source != null) {
            this.emit("hostElementAttached", { el });
            this.ctx.logger.debug("hostElementAttached", { el: (0, play_logs_1.dumpVideoElement)(el) });
        }
    }
    async initVideoEl(elementSupervisor, shouldUpdatePausedOnUnmount = false) {
        // clean old element before initing new one - ensure that the clean is synchronous to the setup and there's
        // no race condition of a clean happening after a new player is set up
        elementSupervisor.muted = this.internalMuted;
        elementSupervisor.volume = this.internalVolume;
        if (shouldUpdatePausedOnUnmount) {
            this.pausedOnUnmount = this.internalPaused;
        }
        if (!this.pausedOnUnmount && this.format !== "flv-http") {
            elementSupervisor.autoplay = true;
        }
        else {
            elementSupervisor.autoplay = false;
        }
        this.startedPlaying = false;
        if (this._poster != null) {
            elementSupervisor.setPoster(this._poster);
        }
        this.ctx.logger.debug("host element is initialized", {
            autoPlay: this.options.autoPlay,
            internalPaused: this.internalPaused,
            internalMuted: this.internalMuted,
        });
        // We want to still reset the srcObject and element.src to null for mp4-ws if they are set, otherwise playback will not work.
        if (this.source != null || this.format === "mp4-ws") {
            if (typeof this.source === "string") {
                elementSupervisor.updateSrc(this.source);
            }
            else if (!(this.format === "mp4-ws" && elementSupervisor.element.src === "")) {
                elementSupervisor.updateSrcObject(this.source);
            }
        }
        if (!this.pausedOnUnmount) {
            this.ctx.logger.info("core: initVideoEl() -> await play()");
            await this.play();
        }
    }
    handleElProgress() {
        if (adapter_1.device.isImplements(feature_1.Feature.DEBUGGING)) {
            this.counters.lastProgress = adapter_1.device.performance.now();
        }
        else {
            this.counters.lastProgress = Date.now();
        }
        this.emit("progress");
    }
    handleElTimeupdate() {
        if (this.elementSupervisor != null) {
            const isVideoPlaying = this.elementSupervisor.isVideoPlaying();
            if (isVideoPlaying && !this.startedPlaying) {
                this.startedPlaying = true;
                this.successfulPlayTimeStamp = Date.now();
                this.emit("videoFirstPlay");
            }
        }
        if (this.successfulPlayTimeStamp != null) {
            const timeElapsed = Math.floor((Date.now() - this.successfulPlayTimeStamp) / 1000);
            if (timeElapsed === 15) {
                this.ctx.logger.debug("Successful playback.");
                this.successfulPlayTimeStamp = null;
            }
        }
        this.counters.fragCounts += 1;
        this.emit("timeupdate");
    }
    resetCounts() {
        this.counters = emptyCounters;
    }
    /**
     * Returns a list of buffer times
     */
    *bufferTimes() {
        var _a, _b, _c;
        const range = (_c = (_b = (_a = this.elementSupervisor) === null || _a === void 0 ? void 0 : _a.buffered) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
        for (let i = 0; this.elementSupervisor != null && i < range; i++) {
            yield [this.elementSupervisor.buffered.start(i), this.elementSupervisor.buffered.end(i)];
        }
    }
    /**
     * gets the details
     * @return {object} Details contract
     */
    get details() {
        var _a;
        return {
            bufferOverflowCount: this.counters.bufferOverflowCount,
            bufferUnderflowCount: this.counters.bufferUnderflowCount,
            downshift: this.counters.downshift,
            errors: (_a = this.errors) !== null && _a !== void 0 ? _a : {},
            muted: this.localAudioMuted,
            segments: this.counters.fragCounts,
            segmentsSize: this.counters.fragSize,
            segmentsDuration: this.counters.fragDuration,
            segmentTotalDownloadTime: this.counters.fragDownloadTime,
            segMaxTime: this.counters.fragMaxTime,
            segMinTime: this.counters.fragMinTime,
            upshift: this.counters.upshift,
            volume: this.localAudioVolume,
        };
    }
    async restart(immediate) {
        var _a;
        this.ctx.logger.info("restart()", { immediate });
        if (this.lockRestart) {
            return;
        }
        this.lockRestart = true;
        this.counters.currentErrorCount = 0;
        this.counters.recentErrorCount = 0;
        this.counters.restartCount += 1;
        if (!immediate) {
            adapter_1.device.clearTimeout((_a = this.clearRestartCount) !== null && _a !== void 0 ? _a : 0);
            this.clearRestartCount = adapter_1.device.setTimeout(() => {
                this.counters.restartCount = 0;
            }, BACK_OFF[BACK_OFF.length - 1] * 2);
            this.emit("restartDriver", {
                timeout: BACK_OFF[Math.min(this.counters.restartCount, BACK_OFF.length - 1)],
            });
            return this.restartTimeout();
        }
        return new Promise((r) => {
            this.stop();
            try {
                this.play(immediate).then(() => {
                    this.lockRestart = false;
                    r();
                });
            }
            catch (err) {
                this.ctx.logger.error("An error occured on the play() request", { err: (0, errors_deprecated_1.wrapNativeError)(err) });
            }
        });
    }
    async restartTimeout() {
        return new Promise((r) => {
            adapter_1.device.setTimeout(() => {
                if (this.isDisposed) {
                    return;
                }
                this.stop();
                try {
                    this.play().then(() => {
                        this.lockRestart = false;
                        r();
                    });
                }
                catch (err) {
                    this.ctx.logger.error("An error occured on the play() request", { err: (0, errors_deprecated_1.wrapNativeError)(err) });
                }
            }, BACK_OFF[Math.min(this.counters.restartCount, BACK_OFF.length - 1)]);
        });
    }
    stop() {
        if (this.elementSupervisor != null) {
            this.elementSupervisor.updateSrc("");
            this.elementSupervisor.updateSrcObject(null);
        }
    }
    async play(immediately = false) {
        var _a;
        this.ctx.logger.info("play()", {
            videoEl: (_a = this.elementSupervisor) === null || _a === void 0 ? void 0 : _a.dumpVideoElement(),
            aggregates: { debug: "play" },
        });
        await this.ready();
        this.ctx.logger.info("ready to play", { aggregates: { debug: "play" } });
        if (this.elementSupervisor == null) {
            this.ctx.logger.warn("a try to play without host element attached", { aggregates: { debug: "play" } });
            return false;
        }
        if (!this.elementSupervisor.isSourceSet()) {
            this.ctx.logger.debug("nothing to play", { aggregates: { debug: "play" } });
            return false;
        }
        if (this.playingPromise == null) {
            this.ctx.logger.setMessageAggregate("debug", "play");
            this.ctx.logger.setMessageAggregate("playImmediately", immediately);
            this.ctx.logger.setMessageAggregate("hasPlayingPromise", this.playingPromise != null);
            this.playingPromise = this.playInternal(this.elementSupervisor, immediately)
                .then(() => {
                var _a;
                this.ctx.logger.trace("playingPromise.then()");
                return !((_a = this.elementSupervisor) === null || _a === void 0 ? void 0 : _a.paused);
            })
                .catch((err) => {
                this.ctx.logger.trace("playingPromise.catch()", { err: (0, errors_deprecated_1.wrapNativeError)(err) });
                return err;
            })
                .finally(() => {
                this.ctx.logger.trace("playingPromise.finally()");
                this.playingPromise = null;
                this.ctx.logger.removeMessageAggregate("debug");
                this.ctx.logger.removeMessageAggregate("playImmediately");
                this.ctx.logger.removeMessageAggregate("hasPlayingPromise");
            });
        }
        return this.playingPromise;
    }
    async playInternal(el, immediately = false) {
        var _a;
        this.ctx.logger.trace("playInternal()");
        if ((0, video_element_1.isMediaStream)(el.srcObject) && ((_a = el.srcObject) === null || _a === void 0 ? void 0 : _a.getTracks().length) <= 0) {
            return;
        }
        if (adapter_1.device.isSafari && this.format === "mp4-hls") {
            this.ctx.logger.info("playInternal safari hls bypassing loadeddata check");
            immediately = true;
        }
        const playingDelegate = immediately ? el.play.bind(el) : helper_1.playOncePossible.bind(null, el, this.ctx.logger);
        try {
            this.ctx.logger.trace("playInternal() trying playDelegate (first time)");
            await playingDelegate();
        }
        catch (err) {
            if (err instanceof Error) {
                const ctx = { inner: (0, errors_deprecated_1.wrapNativeError)(err), playImmediately: immediately, player: this };
                if ((err.name === "AbortError" || err.name === "NotAllowedError") &&
                    !this.localAudioMuted &&
                    this.isImplements(feature_2.Feature.MUTED_AUTOPLAY)) {
                    // try one more time but muted
                    this.ctx.logger.info("attempting forcedMute play", ctx);
                    this.localAudioMuted = true;
                    this.forcedMute = true;
                    try {
                        await playingDelegate();
                        this.ctx.logger.info("playDelegate playing muted due to low page engagement", ctx);
                    }
                    catch (internalErr) {
                        if (internalErr instanceof Error && (0, helper_1.isInLowPowerMode)(internalErr)) {
                            this.lowPowerMode = true;
                            this.ctx.logger.info("iOS Device in low power mode, cannot autoplay", {
                                err: (0, errors_deprecated_1.wrapNativeError)(err),
                                lowPowerMode: this.lowPowerMode,
                            });
                        }
                        else {
                            this.throwErrorDeprecated(new errors_deprecated_1.PlayingIssueError("playDelegate error after mute play", ctx));
                        }
                    }
                }
                else {
                    this.ctx.logger.info("bypassed forcedMute autoplay", {
                        errName: err.name,
                        localAudioMuted: this.localAudioMuted,
                        implementsMutedAutoplay: this.isImplements(feature_2.Feature.MUTED_AUTOPLAY),
                    });
                    // try one more time just in case
                    try {
                        await new Promise((resolve) => {
                            adapter_1.device.setTimeout(resolve, 500);
                        });
                        await playingDelegate();
                        this.ctx.logger.info("playDelegate playing (one more time just in case)", ctx);
                    }
                    catch (internalErr) {
                        if (internalErr instanceof Error && (0, helper_1.isInLowPowerMode)(internalErr)) {
                            this.lowPowerMode = true;
                            this.ctx.logger.info("iOS Device in low power mode, cannot autoplay", {
                                err: (0, errors_deprecated_1.wrapNativeError)(err),
                                lowPowerMode: this.lowPowerMode,
                            });
                        }
                        else {
                            this.throwErrorDeprecated(new errors_deprecated_1.PlayingIssueError("playDelegate error after second try", ctx));
                        }
                    }
                }
            }
            else {
                this.ctx.logger.info("playDelegate unknown error type", {
                    errType: typeof err,
                    player: this,
                });
                throw err;
            }
        }
        finally {
            this.ctx.logger.trace("playInternal() finally");
            this.internalPaused = el.paused;
        }
    }
    async getAuthHeader() {
        if (this.auth != null) {
            try {
                const token = await (0, request_1.getAuthToken)({ ctx: this.ctx, auth: this.auth, reauth: false }); // Where should this reauth option be defined??
                return { Authorization: `bearer ${token}` };
            }
            catch (err) {
                const msg = err instanceof Error ? err.message : "unknown error";
                this.ctx.logger.warn("Authorization error", { err: msg });
                throw err;
            }
        }
        return null;
    }
    /**
     * @param {object} [inputData]
     * @return {void}
     */
    async checkRestart(inputData) {
        // In cases where a machine went to sleep and came back online,
        // the player is in a state where it constantly emits the buffer-stalled
        // error.	The only way I know how to reinstantiate the player is to
        // watch for errors within a timeframe
        const data = inputData;
        this.counters.recentErrorCount += 1;
        adapter_1.device.setTimeout(() => {
            this.counters.recentErrorCount -= 1;
            this.counters.recentErrorCount = Math.max(this.counters.recentErrorCount, 0);
        }, 10000);
        if (this.counters.recentErrorCount >= /* this.options.recoverErrorCount */ 10) {
            data.fatal = true;
        }
        if (this.counters.currentErrorCount >= /* this.options.recoverErrorCount */ 10) {
            data.fatal = true;
        }
        if (data.fatal /* && !this.mediaLoader.vod */) {
            return this.restart(false);
        }
    }
    /**
     *
     * @deprecated use preferredLevel setter instead.
     */
    setPreferredLevel(level) {
        this.preferredLevel = level;
    }
    getClosestQuality(score) {
        return (0, helper_1.findClosestQuality)(score, this.availableQualities);
    }
    isImplements(feature) {
        return this.implementedFeatures.includes(feature);
    }
    replaceManifestUrl(url) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotSupported);
    }
    dispose(reason) {
        this.updatePosterWithLatestFrame();
        super.dispose(reason);
    }
    toJSON() {
        var _a;
        return {
            availableQualities: (0, mobx_1.toJS)(this.availableQualities),
            currentQuality: (0, mobx_1.toJS)(this.currentQuality),
            attached: this.attached,
            counters: this.counters,
            aggregates: {
                support: this.ctx.support.hash,
                autoPlay: this.autoPlay,
                localAudioMuted: this.localAudioMuted,
                localVideoPaused: this.localVideoPaused,
                playingPromise: this.playingPromise == null,
                format: this.format,
                ...(_a = this.elementSupervisor) === null || _a === void 0 ? void 0 : _a.dumpVideoElement(),
            },
        };
    }
}
exports.CorePlayer = CorePlayer;
//# sourceMappingURL=core.js.map